<template>
  <div class="project-orders">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <template v-else>
      <!-- Order Form Section -->
      <div v-if="showOrderForm && editingOrder" class="order-form-section mb-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="mb-0">
              {{ editingOrder.number === '_new' ? $t('order_create') : $t('edit_order') }}
              <span v-if="editingOrder.number !== '_new'">
                #{{ editingOrder.number.toString().padStart(4, '0') }}
              </span>
            </h4>
            <button class="btn btn-sm btn-outline-secondary" @click="closeOrderForm">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="card-body">
            <OrderForm
              :order="editingOrder"
              :projectContext="project"
              :isReadOnly="isBlockReadOnly"
              @update:order="updateEditingOrder"
            />
            <div class="form-actions d-flex justify-content-end align-items-center mt-3">
              <ConfirmDeleteButton
                v-if="showDeleteButton"
                :disabled="isLoading"
                :confirmCallback="handleDeleteOrder"
                class="mr-2"
              />
              <button class="btn btn-default mr-2" @click="closeOrderForm">
                {{ $t('cancel') }}
              </button>
              <button
                v-if="!isBlockReadOnly"
                class="btn btn-inverse"
                @click="handleSaveOrder(editingOrder)"
              >
                {{ editingOrder.number === '_new' ? $t('create') : $t('save') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Orders Table Section -->
      <OrdersTable
        ref="ordersTable"
        :projectNumber="project.number"
        :tableStatePrefix="`project-${project.number}-orders`"
        :showProjectColumn="false"
        :isReadOnly="isBlockReadOnly"
        :canOpenOrderPage="canOpenOrderPage"
        :createOrderRoute="{ name: 'OrderCreatePage', query: { projectId: project.id } }"
        @edit-order="handleEditOrder"
        @create-order="createNewOrder"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import OrdersTable from '@/components/Orders/OrdersTable.vue';
import OrderForm from '@/components/Orders/OrderForm.vue';
import ConfirmDeleteButton from '@/components/ConfirmDeleteButton';
import { is_manager_controlling } from '@/helpers/userCapabilities';
import { coversAccessLevel } from '@/helpers/accessLevel';
import { ORDERS_ACCESS_NODE } from '@/navigation/accessNodes';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-orders ("Bestellungen", see feature page bestellungen): the orders of the project
 * through the paths of the block (`projectNumber` in the order actions). The table stays usable
 * for viewers (filters and paging); every write control is covered by `isBlockReadOnly`.
 *
 * No `BlockFieldset` root: the content is a table and a form, as the view orders.
 */
export default {
  name: 'ProjectOrders',
  components: {
    OrdersTable,
    OrderForm,
    ConfirmDeleteButton,
  },
  mixins: [AccessBlockMixin],
  props: {
    /** The page project; the orders are loaded by `OrdersTable`. */
    project: { type: Object, required: true },
  },
  data() {
    return {
      showOrderForm: true,
      editingOrder: null,
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'getOrderByNumber', 'getAccessLevel']),
    ...mapGetters('auth', ['getCurrentUser', 'isOrganizationRoleNavigationEnabled']),

    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },

    /**
     * The order number opens the view orders only with a level on it; in Matrix-Authz the number
     * is plain text otherwise. Legacy-Authz: `getAccessLevel` returns editor, as today.
     */
    canOpenOrderPage() {
      return coversAccessLevel(this.getAccessLevel(ORDERS_ACCESS_NODE), 'viewer');
    },

    showDeleteButton() {
      if (!this.editingOrder || this.editingOrder.number === '_new' || this.isBlockReadOnly) {
        return false;
      }
      if (this.isOrganizationRoleNavigationEnabled) {
        return true;
      }

      return !is_manager_controlling(this.getCurrentUser);
    },
  },
  methods: {
    ...mapActions(['initOrder', 'createOrder', 'updateOrder', 'deleteOrder', 'fetchOrderByNumber']),

    async handleEditOrder(order) {
      await this.fetchOrderByNumber({
        orderNumber: order.number,
        projectNumber: this.project.number,
      });
      this.editingOrder = this.getOrderByNumber(order.number);
      this.showOrderForm = true;
    },

    updateEditingOrder(order) {
      this.editingOrder = order;
    },

    async handleSaveOrder(order) {
      try {
        if (order.number === '_new') {
          await this.createOrder({ order, projectNumber: this.project.number });
        } else {
          await this.updateOrder({ order, projectNumber: this.project.number });
        }
        this.closeOrderForm();
      } catch (error) {
        console.error('Error saving order:', error);
      }
    },

    async handleDeleteOrder() {
      try {
        await this.deleteOrder({
          orderNumber: this.editingOrder.number,
          projectNumber: this.project.number,
        });
        this.closeOrderForm();
        if (this.$refs.ordersTable) {
          await this.$refs.ordersTable.loadTableData();
        }
      } catch (error) {
        console.error('Error deleting order:', error);
      }
    },

    closeOrderForm() {
      this.showOrderForm = false;
      this.editingOrder = null;
    },

    createNewOrder() {
      this.initOrder();
      const newOrder = this.getOrderByNumber('_new');
      if (newOrder) {
        this.editingOrder = { ...newOrder };
        this.showOrderForm = true;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.order-form-section {
  .card {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;

    .card-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem 1rem;

      h5 {
        color: #495057;
        font-weight: 600;
      }
    }

    .card-body {
      padding: 1rem;
    }
  }

  .form-actions {
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
    margin-top: 1rem;
  }
}

.project-orders {
  .btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;

    &:hover {
      background-color: #6c757d;
      border-color: #6c757d;
      color: white;
    }
  }
}
</style>
